import React from "react";
import { getVastuInfo, getZoneColor } from "@/lib/vastu";
import { getPadaInfo } from "@/lib/padas";
import { Badge } from "@/components/ui/badge";

export default function SectorInspector({ sector, count, rotation }) {
  if (!sector) {
    return (
      <div className="p-5" data-testid="sector-inspector-panel">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Inspector</div>
        <div className="font-display text-lg text-slate-300 mb-1">Hover a sector</div>
        <div className="text-sm text-slate-500 leading-relaxed">
          Move your cursor over any wedge of the compass overlay to see its Vastu attributes, deity, element, and room guidance.
        </div>
        <div className="mt-6 p-4 rounded-lg bg-slate-950/60 border border-slate-800/60">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Current view</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-slate-500 text-xs">Divisions</div><div className="font-mono text-slate-200">{count}</div></div>
            <div><div className="text-slate-500 text-xs">True North</div><div className="font-mono text-slate-200">{rotation.toFixed(1)}°</div></div>
          </div>
        </div>
      </div>
    );
  }
  const info = getVastuInfo(sector.code);
  const zc = getZoneColor(sector.code);
  const pada = count === 32 ? getPadaInfo(sector.code) : null;
  return (
    <div className="p-5 flex flex-col gap-4" data-testid="sector-inspector-panel">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">
          {pada ? `Entrance pada · ${sector.index + 1} / ${count}` : `Sector · ${sector.index + 1} / ${count}`}
        </div>
        <div className="flex items-baseline gap-3">
          <div className="font-display text-4xl font-bold" style={{ color: zc }}>{sector.code}</div>
          <div className="font-display text-xl text-slate-300">{pada ? pada.deity : info.sanskrit}</div>
        </div>
      </div>

      {pada && (
        <div
          className="p-4 rounded-lg border"
          style={{ borderColor: `${pada.gradeMeta.color}55`, background: `${pada.gradeMeta.color}12` }}
          data-testid="pada-guidance-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Entrance effect</div>
            <Badge
              variant="outline"
              className="font-mono text-[10px] border-0"
              style={{ background: `${pada.gradeMeta.color}22`, color: pada.gradeMeta.color }}
              data-testid="pada-grade-badge"
            >
              {pada.gradeMeta.label}
            </Badge>
          </div>
          <div className="text-sm text-slate-200 leading-relaxed" data-testid="pada-effect-text">{pada.effect}</div>
          <div className="mt-3 text-xs text-slate-400">
            Ruled by <span className="text-slate-200 font-medium">{pada.deity}</span> · {info.name} zone ({info.sanskrit})
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Stat label="Deity" value={info.deity} />
        <Stat label="Element" value={info.element} />
        <Stat label="Angle" value={sector.angleRange} mono full />
      </div>

      <div className="p-4 rounded-lg border border-slate-800 bg-slate-950/60">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">{pada ? "Zone guidance" : "Vastu guidance"}</div>
        <div className="text-sm text-slate-300 leading-relaxed">{info.guidance}</div>
      </div>

      <div className="flex items-center gap-2">
        <div className="h-2 w-2 rounded-full" style={{ background: info.color, boxShadow: `0 0 10px ${info.color}` }} />
        <Badge variant="outline" className="border-slate-700 text-slate-300 bg-slate-950/60 font-mono text-[10px]">
          {info.element.toUpperCase()}
        </Badge>
      </div>
    </div>
  );
}

function Stat({ label, value, mono, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`${mono ? "font-mono text-xs" : "font-display text-sm"} text-slate-100`}>{value}</div>
    </div>
  );
}
